import { ActionTree, Module, MutationTree, GetterTree } from "vuex";
import AppState from "../types/AppState";
import User from "@/models/User";
import { API_ENDPOINT } from "@/constants";
import Taxonomy from "@/interfaces/Taxonomy";
import {
  ProductSector,
  ProductCategory,
  ProductClass,
  AdcontrolProfile,
} from "@/models";
import { Route } from "vue-router";
import MediaValue from "@/interfaces/MediaValue";
import moment, { unitOfTime } from "moment";
import store from "@/store";

const apiEndpoint = API_ENDPOINT;
const meEndpoint = apiEndpoint + "/me";

const requestTag = "app";

export const state: AppState = {
  locked: true,
  initialized: false,
  mode: "default",
  user: undefined,
  taxonomies: undefined,
  lastDate: undefined,
};

export const getters: GetterTree<AppState, unknown> = {
  locked(state): boolean {
    return state.locked;
  },

  initialized(state): boolean {
    return state.initialized;
  },

  mode(state): string {
    return state.mode;
  },

  user(state): User | undefined {
    return state.user;
  },

  lastDate(state): string | undefined {
    return state.lastDate;
  },

  // lastDate(state): string | undefined {
  //   return state.user?.profile?.lastDate;
  // },

  taxonomies(state): Array<Taxonomy> | undefined {
    return state.taxonomies;
  },

  parseDateValue(state) {
    // eslint-disable-next-line
    // @ts-ignore
    return (
      date: string,
      format: string,
      // eslint-disable-next-line
      defaultOffset: number = 0,
      defaultUnit: unitOfTime.DurationConstructor = "days"
    ) => {
      if (date) {
        const momentDate = moment(date, format);
        if (
          momentDate.isValid() &&
          momentDate.isSameOrBefore(
            state.lastDate ?? state.user?.profile?.lastDate
          )
        ) {
          return date;
        }
      }
      return moment(state.user?.profile?.lastDate, format)
        .subtract(defaultOffset, defaultUnit)
        .format(format);
    };
  },

  userMedia(state): MediaValue {
    return {
      radio: state.user ? state.user.radio : false,
      tv: state.user ? state.user.tv : false,
    };
  },

  parseOneZeroToBoolean() {
    return (oneZero: string | (string | null)[]) => {
      if (Array.isArray(oneZero)) {
        return false;
      } else if (oneZero) {
        return oneZero === "1";
      }
      return false;
    };
  },

  parseAndCleanMediaValue(state) {
    return (radio: boolean | undefined, tv: boolean | undefined) => {
      const allowedRadio = !!state.user?.radio;
      const allowedTv = !!state.user?.tv;

      radio = radio ?? allowedRadio;
      tv = tv ?? allowedTv;
      radio = allowedRadio ? radio : false;
      tv = allowedTv ? tv : false;

      if (!radio && !tv) {
        return { radio: allowedRadio, tv: allowedTv };
      }

      return { radio, tv };
    };
  },
};

export const mutations: MutationTree<AppState> = {
  setLocked(state, locked: boolean) {
    state.locked = locked;
  },

  setInitialized(state, initialized: boolean) {
    state.initialized = initialized;
  },

  setMode(state, mode: string) {
    state.mode = mode;
  },

  setUser(state, user: User | undefined) {
    state.user = user;
  },

  setLastDate(state, lastDate: string) {
    state.lastDate = moment(lastDate, "DD/MM/YYYY").format("YYYY-MM-DD");
  },

  setTaxonomies(state, taxonomies: Array<Taxonomy> | undefined) {
    state.taxonomies = taxonomies;
  },

  setUserTaxonomies(state, taxonomies: Array<Taxonomy>) {
    if (state.user?.profile?.taxonomies == undefined) {
      return;
    }

    const sectorsMap = {
      categories: new Map(),
      classes: new Map(),
    };

    taxonomies.forEach((sector: Taxonomy) => {
      sector.categories?.forEach((category: Taxonomy) => {
        sectorsMap.categories.set(category.id, sector.id);

        category.classes?.forEach((klass: Taxonomy) => {
          sectorsMap.classes.set(klass.id, sector.id);
        });
      });
    });

    const sectors: string[] = [];

    state.user.profile.taxonomies.forEach(
      (taxonomy: ProductSector | ProductCategory | ProductClass) => {
        if (taxonomy instanceof ProductSector) {
          sectors.push(taxonomy.id);
        }

        if (
          taxonomy instanceof ProductCategory &&
          sectorsMap.categories.has(taxonomy.id)
        ) {
          sectors.push(sectorsMap.categories.get(taxonomy.id));
        }

        if (
          taxonomy instanceof ProductClass &&
          sectorsMap.classes.has(taxonomy.id)
        ) {
          sectors.push(sectorsMap.classes.get(taxonomy.id));
        }
      }
    );

    state.taxonomies = taxonomies.filter((taxonomy: Taxonomy) => {
      return sectors.find((sector) => sector == taxonomy.id) != undefined;
    });
  },
};

export const actions: ActionTree<AppState, unknown> = {
  async initialize({ state, rootGetters }) {
    if (state.initialized) {
      return Promise.resolve();
    }

    if (rootGetters["auth/accessToken"] == undefined) {
      return Promise.reject();
    }

    this.commit("auth/updateRequestHeader");
    this.dispatch("http/register", requestTag);

    try {
      await this.dispatch("loadUser");
      await this.dispatch("loadTaxonomies");
    } catch (e) {
      return Promise.reject(e);
    }

    this.commit("setInitialized", true);
    this.dispatch("unlock");

    return Promise.resolve();
  },

  async loadUser() {
    try {
      const user = await this.dispatch("http/request", {
        tag: requestTag,
        url: `${meEndpoint}.json`,
        method: "GET",
      });

      this.commit("setUser", user);

      return Promise.resolve(user);
    } catch (e) {
      return Promise.reject(e);
    }
  },

  async loadTaxonomies({ state }) {
    try {
      const taxonomies = await this.dispatch("http/request", {
        tag: requestTag,
        url: `${apiEndpoint}/taxonomies.json`,
        method: "GET",
      });

      if (
        state.user &&
        state.user.profile?.taxonomies &&
        state.user.profile.taxonomies.length &&
        state.user.isAdmin == false
      ) {
        this.commit("setUserTaxonomies", taxonomies);
      } else {
        this.commit("setTaxonomies", taxonomies);
      }

      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  },

  lock({ commit }): void {
    commit("setLocked", true);
  },

  unlock({ commit }): void {
    commit("setLocked", false);
  },

  setMode({ commit }, mode: string) {
    commit("setMode", mode);
  },

  routeGuard({ state }, route: Route) {
    if (state.user == undefined) {
      return Promise.reject();
    }

    const admin = route.meta.admin || route.matched[0].meta.admin;

    if (admin && state.user.isAdmin == false) {
      return Promise.reject();
    }

    const feature = route.meta.feature || route.matched[0].meta.feature;

    if (feature && state.user.hasFeature(feature) == false) {
      if (route.meta.allowAgency && !state.user.isAgencyUser) {
        return Promise.reject();
      }
    }

    if (route.name == "Dashboard" && route.query.profile) {
      const profileSlug = route.query.profile;
      const userProfiles = state.user?.adcontrolProfiles;
      const profile = userProfiles?.find((p: AdcontrolProfile) => {
        return p.slug === profileSlug;
      });
      if (profile && state.user?.isAgencyUser && !state.user?.isAdmin) {
        this.commit("setProfile", profile);
      }
    }

    return Promise.resolve();
  },

  logout() {
    this.commit("auth/clearSession");
    this.commit("clearSettings");
  },
};

export const app: Module<AppState, unknown> = {
  namespaced: false,
  state,
  getters,
  mutations,
  actions,
};
